import axios from 'axios'

import { updateLang, DEFAULT_FALLBACK_LANGUAGE } from '../i18n'
import { parseJwt } from './parse-jwt'
import {
	TG_USER_DATA,
	AD_USER_DATA,
	GOOGLE_TOKEN,
	AUTH_ME,
	getAuthMe,
	isGoogleTokenValid,
	clearGoogleSession
} from './auth-storage'

const apiUrl = process.env.REACT_APP_BOT_API_URL

const getAxiosHeaders = async stringifiedTgUserData => {
	return {
		headers: {
			'Telegram-Data': encodeURI(stringifiedTgUserData)
		}
	}
}

export const getAxiosHeadersFromLocalStorage = async () => {
	const stringifiedTgUserData = localStorage.getItem(TG_USER_DATA)
	return await getAxiosHeaders(stringifiedTgUserData)
}

const getUiLang = async stringifiedTgUserData => {
	try {
		const {
			data: { ui_lang = DEFAULT_FALLBACK_LANGUAGE }
		} = await axios.get(`${apiUrl}/investors/me`, await getAxiosHeaders(stringifiedTgUserData))

		return ui_lang
	} catch (err) {
		if (err?.response?.status === 401) throw new Error()
		return DEFAULT_FALLBACK_LANGUAGE
	}
}

const telegramLogin = async tgUserData => {
	if (!tgUserData) return { success: false }

	const stringifiedData = JSON.stringify(tgUserData)
	localStorage.setItem(TG_USER_DATA, stringifiedData)

	try {
		const { status, data } = await axios.post(
			`${apiUrl}/check_auth`,
			null,
			await getAxiosHeaders(stringifiedData)
		)

		if (status !== 200) return { success: false }

		const ui_lang = await getUiLang(stringifiedData)

		localStorage.setItem(AD_USER_DATA, JSON.stringify({ ...data, ui_lang }))

		await updateLang(ui_lang)
		return { success: true, redirectTo: '/' }
	} catch (err) {
		const status = err?.response?.status

		if (status === 403) {
			localStorage.removeItem(TG_USER_DATA)

			return {
				success: true,
				redirectTo: '/unapproved'
			}
		}

		return {
			success: false,
			error: {
				message: 'Login Error',
				name: `Please, contact bot: @${process.env.REACT_APP_BOT_NAME}`
			}
		}
	}
}

const googleLogin = async credential => {
	localStorage.setItem(GOOGLE_TOKEN, credential)

	let profile = {}
	try {
		profile = parseJwt(credential)
	} catch {
		// ignore, /auth/me is the source of truth
	}

	try {
		const { data } = await axios.get(`${apiUrl}/auth/me`, {
			headers: { Authorization: `Bearer ${credential}` }
		})

		localStorage.setItem(
			AUTH_ME,
			JSON.stringify({ ...data, full_name: data.full_name || profile.name, picture: profile.picture })
		)

		return { success: true, redirectTo: '/' }
	} catch (err) {
		localStorage.removeItem(GOOGLE_TOKEN)
		localStorage.removeItem(AUTH_ME)

		return {
			success: false,
			error: {
				message: 'Login Error',
				name: 'This Google account has no access. Use an admin account or one linked to an investor.'
			}
		}
	}
}

export const authProvider = {
	login: async params => {
		// Google Identity Services callback passes { credential, ... }
		if (params?.credential) return await googleLogin(params.credential)
		// Telegram login widget passes the raw tg user data object
		return await telegramLogin(params)
	},
	logout: async () => {
		const googleToken = localStorage.getItem(GOOGLE_TOKEN)

		localStorage.removeItem(TG_USER_DATA)
		localStorage.removeItem(AD_USER_DATA)
		clearGoogleSession()

		if (googleToken) {
			try {
				window.google?.accounts?.id?.revoke(googleToken, () => {})
			} catch {
				// ignore
			}
		}

		return {
			success: true,
			redirectTo: '/login'
		}
	},
	onError: async error => {
		console.error('Auth Provider Error', error)

		return {
			logout: false,
			error
		}
	},
	check: async () => {
		const googleToken = localStorage.getItem(GOOGLE_TOKEN)
		if (googleToken) {
			if (isGoogleTokenValid()) {
				return { authenticated: true }
			}
			// Expired Google session — drop it so the user logs in again instead
			// of getting stuck on a dead admin view.
			clearGoogleSession()
			return {
				authenticated: false,
				logout: true,
				redirectTo: '/login',
				error: { message: 'Session expired', name: 'Please sign in again' }
			}
		}

		const tgUserData = localStorage.getItem(TG_USER_DATA)
		if (tgUserData) {
			const ui_lang = await getUiLang(tgUserData)
			await updateLang(ui_lang)

			return {
				authenticated: true
			}
		}

		console.error('Auth check failed')
		return {
			authenticated: false,
			error: {
				message: 'Login failed',
				name: 'User data not found'
			},
			logout: true,
			redirectTo: '/login'
		}
	},
	getPermissions: async () => getAuthMe()?.role ?? null,
	getIdentity: async () => {
		const googleToken = localStorage.getItem(GOOGLE_TOKEN)
		if (googleToken) {
			const authMe = getAuthMe()
			if (!authMe) return null
			return {
				investor_id: authMe.investor_id,
				full_name: authMe.full_name,
				role: authMe.role,
				is_admin: authMe.role === 'admin',
				email: authMe.email,
				picture: authMe.picture
			}
		}

		const tgUserData = localStorage.getItem(TG_USER_DATA)
		const adUserData = localStorage.getItem(AD_USER_DATA)

		if (tgUserData && adUserData) {
			return { ...JSON.parse(tgUserData), ...JSON.parse(adUserData) }
		}

		return null
	}
}
